/* eslint-disable */
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import DashIcon from "components/icons/DashIcon";
import { useAuth } from "context/AuthContext";
import { useLanguage } from "context/LanguageContext";
import { TranslationKeys as K } from "i18n/translationKeys";
import { RoleSets } from "constants/authorization";
import { MdExpandMore } from "react-icons/md";
// chakra imports

// Menu item groups configuration
const MENU_GROUPS = [
  {
    id: "asset-management",
    label: "Asset Management",
    translationKey: K.MENU_GROUP_ASSET_MANAGEMENT,
    items: ["departments", "categories", "assets", "handovers", "asset-items"]
  },
  {
    id: "production-workers",
    label: "Production & Workers",
    translationKey: K.MENU_GROUP_PRODUCTION_WORKERS,
    items: ["production-lines", "production-orders", "production-steps", "worker-equipment", "equipment-usage", "working-time"]
  },
  {
    id: "training",
    label: "Training",
    translationKey: K.MENU_GROUP_TRAINING,
    items: ["rooms", "courses", "classes", "asset-course-mappings", "student-equipment-assignments"]
  },
  {
    id: "maintenance",
    label: "Maintenance",
    translationKey: K.MENU_GROUP_MAINTENANCE,
    items: ["spare-parts", "maintenance-schedules", "maintenance-records", "maintenance-spare-part-usages"]
  },
  {
    id: "admin",
    label: "Administration",
    translationKey: K.MENU_GROUP_ADMINISTRATION,
    items: ["users", "portal-access", "configuration"]
  }
];

export function SidebarLinks(props) {
  // Chakra color mode
  let location = useLocation();
  const { hasAnyRole } = useAuth();
  const { t } = useLanguage();
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const { routes } = props;

  // verifies if routeName is the one active (in browser input)
  const activeRoute = (routeName) => {
    return location.pathname.includes(routeName);
  };

  const toggleGroup = (groupId) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const canAccessRoute = (route) => {
    const requiredRoles = Array.isArray(route.requiredRoles) ? route.requiredRoles : [];
    // Admin app role can access all pages in the admin layout.
    if (hasAnyRole(RoleSets.Admin)) {
      return true;
    }

    return requiredRoles.length === 0 ||
      hasAnyRole(requiredRoles);
  };

  const getGroupRoutes = (group) => {
    return routes.filter(
      (route) =>
        (route.layout === "/admin" ||
          route.layout === "/auth" ||
          route.layout === "/rtl") &&
        route.sidebar !== false &&
        group.items.includes(route.path) &&
        canAccessRoute(route)
    ).sort(
      (a, b) => group.items.indexOf(a.path) - group.items.indexOf(b.path)
    );
  };

  useEffect(() => {
    const activeGroups = MENU_GROUPS
      .filter((group) => getGroupRoutes(group).some((route) => activeRoute(route.path)))
      .map((group) => group.id);

    setExpandedGroups((prev) => {
      const next = new Set(prev);
      activeGroups.forEach((groupId) => next.add(groupId));
      return next;
    });
  }, [location.pathname]);

  const renderMenuItem = (route) => {
    return (
      <Link key={route.path} to={route.layout + "/" + route.path}>
        <div className="relative mb-3 flex hover:cursor-pointer">
          <li className="my-[3px] flex cursor-pointer items-center px-8">
            <span
              className={`${
                activeRoute(route.path) === true
                  ? "font-bold text-brand-500 dark:text-white"
                  : "font-medium text-gray-600"
              }`}
            >
              {route.icon ? route.icon : <DashIcon />}
            </span>
            <p
              className={`leading-1 ml-4 flex ${
                activeRoute(route.path) === true
                  ? "font-bold text-navy-700 dark:text-white"
                  : "font-medium text-gray-600"
              }`}
            >
              {t(route.translationKey, route.name)}
            </p>
          </li>
          {activeRoute(route.path) ? (
            <div className="absolute right-0 top-px h-9 w-1 rounded-lg bg-brand-500 dark:bg-brand-400" />
          ) : null}
        </div>
      </Link>
    );
  };

  const renderGroup = (group) => {
    const groupRoutes = getGroupRoutes(group);

    if (groupRoutes.length === 0) return null;

    const isExpanded = expandedGroups.has(group.id);
    const hasActiveItem = groupRoutes.some((route) => activeRoute(route.path));

    return (
      <div key={group.id}>
        <button
          onClick={() => toggleGroup(group.id)}
          className={`w-full flex items-center justify-between px-8 py-2 mb-2 rounded transition-colors ${
            hasActiveItem
              ? "bg-blue-50 dark:bg-blue-900/20"
              : "hover:bg-gray-100 dark:hover:bg-white/5"
          }`}
        >
          <span
            className={`flex items-center gap-3 font-medium ${
              hasActiveItem
                ? "text-navy-700 dark:text-white"
                : "text-gray-600 dark:text-gray-400"
            }`}
          >
            {t(group.translationKey, group.label)}
          </span>
          <MdExpandMore
            className={`h-5 w-5 transition-transform ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </button>
        {isExpanded && (
          <div className="space-y-0">
            {groupRoutes.map((route) => renderMenuItem(route))}
          </div>
        )}
      </div>
    );
  };

  // Dashboard and other ungrouped pages
  const dashboardRoute = routes.find(
    (r) =>
      (r.layout === "/admin" || r.layout === "/auth" || r.layout === "/rtl") &&
      r.sidebar !== false &&
      r.path === "default" &&
      canAccessRoute(r)
  );

  return (
    <>
      {dashboardRoute && renderMenuItem(dashboardRoute)}
      {MENU_GROUPS.map((group) => renderGroup(group))}
    </>
  );
}

export default SidebarLinks;
